import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';

// Lazy load Layout component
const Layout = () => import('../components/Layout.vue');

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '../views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: Layout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/dashboard',
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
        },
        {
          path: 'tenants',
          name: 'tenants',
          component: () => import(/* webpackChunkName: "tenants" */ '../views/Tenants.vue'),
        },
        {
          path: 'tenants/create',
          name: 'create-tenant',
          component: () =>
            import(/* webpackChunkName: "create-tenant" */ '../views/CreateTenant.vue'),
        },
        {
          path: 'tenants/:id',
          name: 'tenant-detail',
          component: () => import(/* webpackChunkName: "view-tenant" */ '../views/ViewTenant.vue'),
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import(/* webpackChunkName: "billing" */ '../views/Billing.vue'),
        },
        {
          path: 'billing/plans/:id/edit',
          name: 'edit-billing-plan',
          component: () =>
            import(/* webpackChunkName: "edit-billing-plan" */ '../views/EditBillingPlan.vue'),
        },
        {
          path: 'billing/invoices/:id',
          name: 'invoice-detail',
          component: () =>
            import(/* webpackChunkName: "invoice-detail" */ '../views/InvoiceDetails.vue'),
        },
        {
          path: 'platform-users',
          name: 'platform-users',
          component: () =>
            import(/* webpackChunkName: "platform-users" */ '../views/PlatformUsers.vue'),
        },
        {
          path: 'platform-users/create',
          name: 'create-platform-user',
          component: () =>
            import(
              /* webpackChunkName: "create-platform-user" */ '../views/CreatePlatformUser.vue'
            ),
        },
        {
          path: 'platform-users/:id',
          name: 'view-platform-user',
          component: () =>
            import(/* webpackChunkName: "view-platform-user" */ '../views/ViewPlatformUser.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
        },
        {
          path: 'settings/modules',
          name: 'modules',
          component: () => import(/* webpackChunkName: "modules" */ '../views/Modules.vue'),
        },
        {
          path: 'settings/modules/create',
          name: 'create-module',
          component: () =>
            import(/* webpackChunkName: "create-module" */ '../views/CreateModule.vue'),
        },
        {
          path: 'settings/modules/:id/edit',
          name: 'edit-module',
          component: () => import(/* webpackChunkName: "edit-module" */ '../views/EditModule.vue'),
        },
        {
          path: 'settings/modules/:id/assign',
          name: 'assign-module',
          component: () =>
            import(/* webpackChunkName: "assign-module" */ '../views/AssignModule.vue'),
        },
        {
          path: 'settings/permissions',
          name: 'permissions',
          component: () => import(/* webpackChunkName: "permissions" */ '../views/Permissions.vue'),
        },
        {
          path: 'settings/email-templates/:id/edit',
          name: 'edit-email-template',
          component: () =>
            import(/* webpackChunkName: "edit-email-template" */ '../views/EditEmailTemplate.vue'),
        },
        {
          path: 'settings/curriculums/:id',
          name: 'curriculum-detail',
          component: () =>
            import(/* webpackChunkName: "curriculum-detail" */ '../views/CurriculumDetail.vue'),
        },
        {
          path: 'settings/academic-years',
          name: 'academic-years',
          component: () =>
            import(/* webpackChunkName: "academic-years-list" */ '../views/AcademicYearsList.vue'),
        },
        {
          path: 'settings/academic-years/create',
          name: 'create-academic-year',
          component: () =>
            import(
              /* webpackChunkName: "create-academic-year" */ '../views/CreateAcademicYear.vue'
            ),
        },
        {
          path: 'settings/academic-years/:id/assessments/create',
          name: 'create-assessment',
          component: () =>
            import(/* webpackChunkName: "create-assessment" */ '../views/CreateAssessment.vue'),
        },
        {
          path: 'settings/academic-years/:id',
          name: 'view-academic-year',
          component: () =>
            import(/* webpackChunkName: "view-academic-year" */ '../views/ViewAcademicYear.vue'),
        },
        {
          path: 'settings/academic-years/:id/edit',
          name: 'edit-academic-year',
          component: () =>
            import(/* webpackChunkName: "edit-academic-year" */ '../views/EditAcademicYear.vue'),
        },
      ],
    },
  ],
});

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' });
  } else {
    next();
  }
});

export default router;
